import React from 'react';
import './AboutUs.css';

const AboutUs = () => {
  return (
    <div className="about-us">
      <div className="container">
        {/* Intro Section */}
        <section className="about-intro">
          <h1 className="about-title">❤️ ABOUT NAVODAYA TRENDZ</h1>
          <p className="about-subtitle">More Than Merchandise. A Community. A Legacy.</p>
          
          <div className="intro-text">
            <p>Every Navodayan carries a story.</p>
            <p>A story of leaving home at a young age.</p>
            <p>A story of hostel life, friendships, morning assemblies, house competitions, late-night conversations, shared dreams, and memories that last a lifetime.</p>
            <p>No matter where life takes us—whether we become entrepreneurs, engineers, teachers, doctors, civil servants, artists, or professionals—we all share one identity:</p>
            <h2 className="identity-text"># We Are Navodayans.</h2>
            <p>Navodaya Trendz was born from this identity.</p>
          </div>
        </section>

        <hr className="divider" />

        {/* Our Story */}
        <section className="about-section">
          <h2 className="section-heading">🎓 Our Story</h2>
          <p>Navodaya Trendz (NTz) is India's first dedicated merchandise and community platform built exclusively for the Jawahar Navodaya Vidyalaya ecosystem.</p>
          <p>We started with a simple belief:</p>
          <blockquote className="blockquote">
            Every Navodayan deserves a way to celebrate their journey, reconnect with their community, and proudly wear the identity that shaped them.
          </blockquote>
          <p>What began as a vision to create meaningful JNV merchandise has evolved into something much bigger—a platform that connects alumni, students, schools, organizers, and professionals across the Navodaya family.</p>
        </section>

        <hr className="divider" />

        {/* Platform for Every Navodayan */}
        <section className="about-section">
          <h2 className="section-heading">🌍 A Platform for Every Navodayan</h2>
          <p>Today, Navodayans are making an impact across India and around the world.</p>
          <p>Yet one thing remains constant:</p>
          <p className="highlight-text">The bond we built inside JNV.</p>
          <p>Navodaya Trendz exists to strengthen that bond by creating products, experiences, and opportunities that bring the community together.</p>
          <p>Whether you're attending your first alumni meet, organizing a reunion, launching a regional alumni chapter, or simply looking for a T-shirt that reminds you of your school days—we're here to help you celebrate your story.</p>
        </section>

        <hr className="divider" />

        {/* What We Create */}
        <section className="about-section">
          <h2 className="section-heading">👕 What We Create</h2>
          <p>We design and deliver premium merchandise inspired by the Navodaya spirit.</p>
          
          <div className="products-grid">
            <div className="product-category">
              <h3>Apparel</h3>
              <ul>
                <li>Round Neck T-Shirts</li>
                <li>Premium Polo T-Shirts</li>
                <li>Hoodies</li>
                <li>Sweatshirts</li>
                <li>Alumni Meet Collections</li>
              </ul>
            </div>
            
            <div className="product-category">
              <h3>Alumni Meet Essentials</h3>
              <ul>
                <li>Event Kits</li>
                <li>Customized Badges</li>
                <li>ID Cards & Lanyards</li>
                <li>Selfie Frames</li>
                <li>Banners & Standees</li>
                <li>Welcome Kits</li>
              </ul>
            </div>
            
            <div className="product-category">
              <h3>Personalized Merchandise</h3>
              <ul>
                <li>Mugs</li>
                <li>Bottles</li>
                <li>Diaries</li>
                <li>Keychains</li>
                <li>Wristbands</li>
                <li>Awards & Mementos</li>
              </ul>
            </div>
          </div>
          
          <p className="product-footer">Every product is thoughtfully designed to represent the pride, values, and memories of being a Navodayan.</p>
        </section>

        <hr className="divider" />

        {/* Beyond Merchandise */}
        <section className="about-section">
          <h2 className="section-heading">🤝 Beyond Merchandise</h2>
          <p>Navodaya Trendz is not just an e-commerce brand.</p>
          <p>We are building a digital ecosystem where Navodayans can:</p>
          
          <ul className="features-list">
            <li>Discover Alumni Meets</li>
            <li>Register for Events</li>
            <li>Connect with Batchmates</li>
            <li>Join Regional Alumni Networks</li>
            <li>Attend Career Guidance Sessions</li>
            <li>Learn from Successful Alumni</li>
            <li>Celebrate Achievements</li>
            <li>Build Meaningful Professional Connections</li>
          </ul>
          
          <p>Our goal is simple:</p>
          <h2 className="identity-text"># To bring every Navodayan closer together.</h2>
        </section>

        <hr className="divider" />

        {/* Our Vision */}
        <section className="about-section">
          <h2 className="section-heading">🚀 Our Vision</h2>
          <p>To become the world's largest Navodayan community platform—connecting students, alumni, educators, and professionals through merchandise, networking, events, and shared experiences.</p>
          <p>We envision a future where every JNV has a stronger alumni network, every alumnus stays connected, and every student benefits from the strength of the Navodaya community.</p>
        </section>

        <hr className="divider" />

        {/* Why We Do This */}
        <section className="about-section">
          <h2 className="section-heading">💙 Why We Do This</h2>
          <p>Because Navodaya is not just a school.</p>
          <p>It is:</p>
          <ul className="why-list">
            <li>A second home</li>
            <li>A lifelong network</li>
            <li>A shared identity</li>
            <li>A family spread across the world</li>
          </ul>
          <p>And some memories deserve to be carried forever.</p>
        </section>

        <hr className="divider" />

        {/* Our Promise */}
        <section className="about-section">
          <h2 className="section-heading">✨ Our Promise</h2>
          <p>At Navodaya Trendz, we are committed to:</p>
          <ul className="promises-list">
            <li>✔ Premium Quality Products</li>
            <li>✔ Meaningful & Original Designs</li>
            <li>✔ Reliable Service & Timely Delivery</li>
            <li>✔ Dedicated Alumni Meet Support</li>
            <li>✔ Continuous Innovation for the Navodaya Community</li>
          </ul>
        </section>

        <hr className="divider" />

        {/* Stats Section */}
        <section className="about-section stats-section">
          <h2 className="section-heading">🏆 650+ Schools. One Identity.</h2>
          <p>Thousands of alumni.</p>
          <p>Millions of memories.</p>
          <p>One community.</p>
          <h2 className="identity-text"># Made by Navodayans. For Navodayans.</h2>
          <p className="tagline">Wear Your Identity. Relive Your Memories. Stay Connected.</p>
          <h3 className="welcome-text">Welcome to Navodaya Trendz. ❤️🎓</h3>
        </section>

        <hr className="divider" />

        {/* Hero Quote */}
        <section className="about-section quote-section">
          <blockquote className="hero-quote">
            "Navodaya gave us memories. Navodaya Trendz helps us carry them forward."
          </blockquote>
        </section>
      </div>
    </div>
  );
};

export default AboutUs;
